import { css } from 'astroturf';
import React, { useId } from 'react';
import { Download } from 'react-bytesize-icons';
import InfoDialog from './_infoDialog';
import { TypeBadge } from './_typeBadge';
import { createDialogId, getSnippetCode, getSnippetCodeLanguage } from './_catalogCardUtils';
import { updateDialogSearchParams } from './_dialogSearchParams';

const styles = css`
  .infoButton {
    border: 0;
    background: #0a153011;
    color: #39728d;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 100px;
    padding: 0;
    line-height: 1;
    font-size: 0.8rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition:
      0.15s ease-in-out background,
      0.15s ease-in-out color;

    &:hover,
    &:focus-visible {
      background: #0a1530;
      color: #fff;
    }
  }

  .cardHeader {
    position: relative;
    margin-top: 2rem;
    padding: 0rem 2rem 1.125rem;

    h4 {
      font-size: 1.125rem;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      margin: 0;

      small {
        font-size: 0.75em;
        color: #39728d;
        font-weight: 400;
      }
    }
  }

  .infobar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
  }

  .categories {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    padding-right: 0;
  }

  .category {
    font-size: 10px;
    border-radius: 100px;
    padding: 0.1rem 0.55rem;
    background: #00aeff21;
    color: #333;
  }

  .author {
    margin: 0;
    line-height: 1.25;
    font-size: 0.875rem;
    flex: 1 0 auto;

    b {
      font-weight: 500;
      color: #39728d;
    }
  }

  .dialogCode {
    margin: 0 !important;
    max-height: 42svh;
    overflow: auto;
    padding: 2rem;
    border-radius: 0;
    color-scheme: dark;

    code {
      display: block;
      line-height: 1.45;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  .dialogActions {
    padding: 0 2rem 1rem;
  }

  .download {
    border: 0;
    padding: 0.6rem 0.75rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    background: rgb(16, 125, 181);
    color: #fff;
    transition: background 0.15s ease-in-out;

    &:hover,
    &:focus-visible {
      background: #008300;
    }

    svg {
      width: 14px;
      height: 14px;
      stroke-width: 3px;
    }
  }
`;

function createSnippetParamValue(snippet) {
  const slug = String(snippet.name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (slug) {
    return slug;
  }

  return String(snippet.id);
}

export default function SnippetInfoDialog({
  snippet,
  normalizeType,
  onDownload,
  activeSnippet = null,
  activeCollection = null,
  collectionName = null,
  renderTrigger = null,
}) {
  const instanceId = useId();
  const codeLanguage = getSnippetCodeLanguage(snippet.language);
  const code = getSnippetCode(snippet);
  const snippetParamValue = createSnippetParamValue(snippet);

  const isOpen = collectionName
    ? activeSnippet === snippetParamValue && activeCollection === String(collectionName)
    : activeSnippet === snippetParamValue && !activeCollection;

  const handleOpenChange = (nextIsOpen) => {
    updateDialogSearchParams((params) => {
      if (nextIsOpen) {
        if (collectionName) {
          params.set('collection', String(collectionName));
        } else {
          params.delete('collection');
        }

        params.set('snippet', snippetParamValue);
        return;
      }

      const currentCollection = params.get('collection');
      const isMatchingCollection = collectionName ? currentCollection === String(collectionName) : !currentCollection;

      if (params.get('snippet') === snippetParamValue && isMatchingCollection) {
        params.delete('snippet');
      }
    }, nextIsOpen);
  };

  return (
    <InfoDialog
      id={createDialogId('snippet-dialog', `${snippet.id}-${instanceId}`)}
      triggerClassName={styles.infoButton}
      triggerLabel={`Show details for ${snippet.name}`}
      triggerContent="i"
      renderTrigger={renderTrigger}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className={styles.cardHeader}>
        <h4>
          <TypeBadge language={snippet.language} normalizeType={normalizeType} /> {snippet.name}
        </h4>
        {snippet.description && <p>{snippet.description}</p>}

        <div className={styles.infobar}>
          {snippet.author !== 'Unknown' ? (
            <p className={styles.author}>
              By{' '}
              {snippet.authorUrl ? (
                <a href={snippet.authorUrl} target="_blank" rel="noopener noreferrer">
                  <b>{snippet.author}</b>
                </a>
              ) : (
                <b>{snippet.author}</b>
              )}
            </p>
          ) : null}

          {!!(snippet.categories || []).length && (
            <div className={styles.categories}>
              {(snippet.categories || []).map((category) => (
                <span key={category} className={styles.category}>
                  {category}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.dialogActions} data-card="snippet">
        <button
          className={styles.download}
          type="button"
          onClick={() => onDownload(snippet)}
          data-action="download-snippet"
        >
          <span>Download</span>
          <Download />
        </button>
        <pre hidden>{JSON.stringify(snippet)}</pre>
      </div>
      <pre className={[styles.dialogCode, `language-${codeLanguage}`].join(' ')}>
        <code className={`language-${codeLanguage}`}>{code}</code>
      </pre>
    </InfoDialog>
  );
}
